import { create } from 'zustand';
import { toast } from 'sonner';
import { userService } from '../services/user.service';
import { onlyDigits } from '../lib/utils';
import { useAuthStore } from './useAuthStore';

export const initialBancoData = {
    nomeBanco: "",
    agencia: "",
    conta: "",
    tipoPix: "cpf",
    chavePix: "",
    tipoConta: "corrente",
};

const normalizeUserData = (data) => {
    if (!data) return null;
    return {
        ...data,
        processos: data.processos || data.processos_cedente || []
    };
};

export const useFormStore = create((set, get) => ({
    userData: null,
    bancoData: initialBancoData,
    selectedProcessIds: [],
    processTypes: {},
    processPercentages: {},
    isSubmitting: false,
    cessao: false,

    setUserData: (data) => set({ userData: normalizeUserData(data) }),
    setBancoData: (data) => set({ bancoData: data }),
    setCessao: (value) => set({ cessao: value }),
    setProcessPercentage: (id, percentage) => set((state) => ({
        processPercentages: {
            ...state.processPercentages,
            [id]: percentage
        }
    })),

    handleUserDataChange: (field, value) => set((state) => ({
        userData: normalizeUserData({ ...state.userData, [field]: value })
    })),

    handleBancoChange: (field, value) => {
        set((state) => ({
            bancoData: { ...state.bancoData, [field]: value }
        }));
    },

    setSelectedProcessIds: (ids) => set({ selectedProcessIds: ids }),

    toggleProcessType: (id, isJuri) => set((state) => ({
        processTypes: {
            ...state.processTypes,
            [id]: isJuri ? 'acordo_cessao' : 'acordo'
        }
    })),

    initializeProcesses: () => {
        const { userData, selectedProcessIds, processTypes } = get();
        if (!userData || !userData.processos) return;

        const newIds = userData.processos.map(p => p.id);
        const hasNewIds = selectedProcessIds.length === 0 && newIds.length > 0;

        let newTypes = { ...processTypes };
        let hasChangesTypes = false;

        userData.processos.forEach(p => {
            if (!newTypes[p.id]) {
                newTypes[p.id] = 'acordo';
                hasChangesTypes = true;
            }
        });

        if (hasNewIds || hasChangesTypes) {
            set({
                selectedProcessIds: hasNewIds ? newIds : selectedProcessIds,
                processTypes: hasChangesTypes ? newTypes : processTypes
            });
        }
    },

    clearForm: () => set({
        userData: null,
        bancoData: initialBancoData,
        selectedProcessIds: [],
        processTypes: {},
        cessao: false
    }),

    submitForm: async (tipoAceite, isFalecido) => {
        const state = get();
        const { userData, bancoData, selectedProcessIds, processTypes } = state;
        set({ isSubmitting: true });
        try {
            const [conta, digito] = (bancoData?.conta || "").split("-");
            const banco = bancoData?.nomeBanco || "";
            const isUpdateOnly = tipoAceite === "update_only";

            const payload = {
                cpf: onlyDigits(userData?.cpf || ""),
                nome: userData?.nomeCompleto || "",
                rg: userData?.rg || "",
                telefone: userData?.telefone || "",
                email: userData?.email || "",
                banco: banco,
                agencia: bancoData?.agencia.replace(/[xX]/g, "0") || "",
                conta_corrente: (conta || "").replace(/[xX]/g, "0") || "",
                digito_conta: (digito || "").replace(/[xX]/g, "0") || "",
                pix: bancoData?.chavePix || "",
                tipo_pix: bancoData?.tipoPix || undefined,
                endereco_logradouro: userData?.logradouro || "",
                endereco_numero: userData?.numero || "",
                endereco_complemento: userData?.complemento || "",
                endereco_bairro: userData?.bairro || "",
                endereco_cidade: userData?.cidade || "",
                endereco_estado: userData?.estado || "",
                endereco_cep: userData?.cep || "",
                tipo_conta: bancoData?.tipoConta || "corrente",
                tipo_aceite: isUpdateOnly ? null : tipoAceite,
                processes_config: isUpdateOnly ? [] : selectedProcessIds.map(id => ({
                    id,
                    tipo_aceite: processTypes[id] || 'acordo'
                })),
                estado_civil: userData?.estadoCivil || "",
                nome_conjuge: userData?.nome_conjuge || "",
                cpf_conjuge: onlyDigits(userData?.cpf_conjuge || ""),
                data_nascimento: userData?.data_nascimento || "",
                falecido: isFalecido !== userData?.falecido ? isFalecido : userData?.falecido,
                selected_process_ids: isUpdateOnly ? [] : selectedProcessIds,
            };

            const isAdmin = useAuthStore.getState().isAdminMode;
            await userService.registerCedente(payload, isAdmin);

            if (isUpdateOnly) {
                toast.success("Dados cadastrais salvos com sucesso.");
            } else if (!isFalecido) {
                toast.success("Dados salvos e contratos enviados para assinatura!");
            } else {
                toast.success("Dados salvos com sucesso.");
            }

            return true;
        } catch (e) {
            toast.error(e.message || "Erro desconhecido ao salvar os dados.");
            return false;
        } finally {
            set({ isSubmitting: false });
        }
    },

    submitCessao: async (processIds = null) => {
        const state = get();
        const { userData, processTypes, selectedProcessIds } = state;
        const cpf = onlyDigits(userData?.cpf || "");

        set({ isSubmitting: true });
        try {
            const processesToCessao = Array.isArray(processIds)
                ? processIds
                : selectedProcessIds.filter(id => processTypes[id] === 'acordo_cessao');

            if (processesToCessao.length === 0) return true;

            const { processService } = await import('../services/process.service');

            for (const id of processesToCessao) {
                const percentage = state.processPercentages[id] || 100.0;
                await processService.createCessao(cpf, id, percentage);
                toast.success(`Cessão solicitada para o processo ${id} (${percentage}%)`);
            }

            return true;
        } catch (e) {
            toast.error(e.message || "Erro ao solicitar cessão.");
            return false;
        } finally {
            set({ isSubmitting: false });
        }
    }
}));
